import React from 'react';
import styles from './step2.module.scss';
import Prompt from 'components/form/prompt/prompt';
import PhoneNumber from 'components/form/phoneNumber/phoneNumber';
import * as Yup from 'yup';
import StateDropdown from 'components/form/stateDropdown/stateDropdown';
import FormBuilder from 'components/form/builder/builder';
import * as Constants from '../constants';

// Masking via react-input-mask takes care of restricting input to digits
// Regex here matches something like (111) 111-1111
const phoneRegExp = /^\(\d{3}\)[\s]?\d{3}-\d{4}$/;

const schemaShape = {
  address1: Yup.string()
    .trim()
    .max(
      Constants.MAX_CHARS_FOR_FIELD,
      `Please enter less than ${Constants.MAX_CHARS_FOR_FIELD} characters!`
    )
    .required('Required'),
  City: Yup.string()
    .trim()
    .matches(Constants.LETTERS_AND_SPACES, {
      message: Constants.LETTERS_AND_SPACES_MESSAGE
    })
    .max(
      Constants.MAX_CHARS_FOR_FIELD,
      `Please enter less than ${Constants.MAX_CHARS_FOR_FIELD} characters!`
    )
    .required('Required'),
  State: Yup.string().required('Required'),
  Zip: Yup.string()
    .trim()
    .matches(/^[0-9]{5}(?:-[0-9]{4})?$/, { message: 'Invalid Zip Code' })
    .required('Required'),
  SeparateAddress: Yup.boolean().required('Required'),
  ContactName: Yup.string()
    .trim()
    .matches(Constants.LETTERS_AND_SPACES, {
      message: Constants.LETTERS_AND_SPACES_MESSAGE
    })
    .required('Required'),
  ContactEmail: Yup.string()
    .trim()
    .email('Invalid email')
    .required('Required'),
  PhoneNumber: Yup.string()
    .trim()
    .matches(phoneRegExp, 'Please enter a valid 10-digit phone number')
    .required('Required'),
  MobileNumber: Yup.string()
    .trim()
    .matches(
      phoneRegExp,
      'Please enter a valid 10-digit mobile number'
    )
};

function formatPhoneNumber(phoneNumberString) {
  var cleaned = ('' + phoneNumberString).replace(/\D/g, '');
  var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  return '';
}


const Step2 = props => {
  const Address1 = props.response?.result?.entityinfo?.ContactInfo?.Address1 || '';
  const initialValues = {
    address1: Address1,
    address2: '',
    City: props.response?.result?.entityinfo?.ContactInfo?.City || '',
    State: props.response?.result?.entityinfo?.ContactInfo?.State || '',
    Zip: props.response?.result?.entityinfo?.ContactInfo?.Zip || '',
    MailingAddress1: '',
    MailingAddress2: '',
    MailingCity: '',
    MailingState: '',
    MailingZip: '',
    SeparateAddress: false, // Default 'false' means NO selected by default on YES/NO prompt
    PhoneNumber: formatPhoneNumber(props.response?.result?.entityinfo?.ContactInfo?.PhoneNumber || ''),
    MobileNumber: '',
    ContactName: props.response?.result?.entityinfo?.ContactInfo?.NameOfContact || '',
    ContactEmail: props.response?.result?.entityinfo?.ContactInfo?.Email || '',
    ...props.values
  };
  const elements = [
    {
      children: [
        { name: 'address1', placeholder: 'Street Address', col: '12' },
        {
          name: 'address2',
          placeholder: 'Apt, Suite, Building (optional)',
          col: '12'
        },
        { name: 'City', placeholder: 'City', col: '4' },
        {
          name: 'State',
          Component: StateDropdown,
          col: '4'
        },
        { name: 'Zip', placeholder: 'Zip Code', col: '4' }
      ],
      label: 'What is the address associated with the entity?',
      name: 'Address',
      googleIntegration: true // Uses places API to autocomplete addresses
    },
    {
      Component: Prompt,
      label: 'Do you have a separate mailing address?',
      name: 'SeparateAddress',
      props: {
        options: [
          { name: 'Yes', value: true },
          { name: 'No', value: false }
        ]
      },
      conditionaler: true // Element below is conditional on this one
    },
    {
      children: [
        { name: 'MailingAddress1', placeholder: 'Street Address', col: '12' },
        {
          name: 'MailingAddress2',
          placeholder: 'Apt, Suite, Building (optional)',
          col: '12'
        },
        { name: 'MailingCity', placeholder: 'City', col: '4' },
        {
          name: 'MailingState',
          Component: StateDropdown,
          placeholder: 'State',
          col: '4'
        },
        { name: 'MailingZip', placeholder: 'Zip Code', col: '4' }
      ],
      label: 'What is your mailing address?',
      conditional: {
        name: 'SeparateAddress',
        value: true
      },
      name: 'MailingAddress',
      googleIntegration: true
    },
    {
      children: [
        {
          Component: PhoneNumber,
          label: 'Phone Number',
          name: 'PhoneNumber',
          col: '6'
        },
        {
          Component: PhoneNumber,
          label: 'Mobile Number',
          name: 'MobileNumber',
          col: '6'
        }
      ]
    },
    {
      label: 'What is the name of the designated contact?',
      name: 'ContactName',
      placeholder: 'Enter Name'
    },
    {
      label: 'What is the email of the designated contact?',
      name: 'ContactEmail',
      placeholder: 'Enter Email',
      props: {
        type: 'email'
      }
    }
  ];

  return (
    <div className={styles.wrapper}>
      {/* The FormBuilder component renders as Formik component. */}
      <FormBuilder
        elements={elements}
        schemaShape={schemaShape}
        initialValues={initialValues}
        updateValues={props.updateValues}
        onNext={props.onNext}
        step={props.step}
        setStep={props.setStep}
      />
    </div>
  );
};

export default Step2;
